/**
 * Edjin (the EDJIN brand) homepage copy: a marketing page with a contact form, rendered by EdjinHome.jsx rather than
 * the product homepage, since Edjin sells the platform to publishers and has no sign-in, trial or pricing.
 *
 * Carried across from the Struts page `src/main/webapp/home/edjin/index.jsp`: order and words kept, its images reused
 * from `home/edjin/img`. The contact form is simpler than the old one (`contactEmailForm.jsp`, which asked support
 * questions about textbooks and activation codes); it posts to `/web/ui/home/public/contact`, which emails the brand's
 * `emailTo`.
 */
const IMG = '/home/edjin/img';

export const edjinContent = {
    productName: 'Edjin',
    homepage: 'edjin',

    links: {home: '/'},

    // No `logo`: the header and footer keep the HOTmaths homepage's Cambridge and HOTmaths logos.

    /** Edjin blue (components/brandThemes.module.css). */
    theme: 'edjin',

    copyright: 'Edjin',

    intro: {
        heading: 'The Education Engine',
        standfirst: 'An interactive online platform for publishers, school systems and academic institutions.',
        primaryAction: {label: 'See one of our products in action', href: '#ch-home-video'},
        secondaryAction: {label: 'Contact us', href: '#ch-edjin-contact'},
        // The old page's banner (home/edjin/css/style.css), shared with the USA homepage.
        backgroundImage: '/home/usa/img/background.png',
    },

    features: {
        heading: 'Features',
        standfirst: 'Discover how the Edjin education engine can provide curriculum-based teaching, learning and '
            + 'assessment material using meaningful and engaging activities.',
        items: [
            {title: 'Multi-device design', image: `${IMG}/s1.jpg`,
                body: 'An adaptive design with all interactive media ready for desktops and tablets.'},
            {title: 'Existing content libraries', image: `${IMG}/s2.jpg`,
                body: 'Make use of our existing mathematics and science content containing interactive widgets, '
                    + 'videos and more, mapped to your curriculum.'},
            {title: 'LMS and a test generator', image: `${IMG}/s3.jpg`,
                body: 'The platform tracks all student activity on the site, including homework tasks, revision '
                    + 'activities and assessment tests.'},
        ],
    },

    /** Products built on Edjin. Senior Maths had no link on the old page either. */
    products: {
        heading: 'Products',
        items: [
            {name: 'Cambridge HOTmaths', image: `${IMG}/portfolio/hm.png`, href: 'https://www.hotmaths.com.au'},
            {name: 'Cambridge Dynamic Science', image: `${IMG}/portfolio/ds.png`,
                href: 'https://dynamicscience.cambridge.edu.au'},
            {name: 'Cambridge HOTmaths Fundamentals', image: `${IMG}/portfolio/fu.png`,
                href: 'https://www.hotmaths.com.au'},
            {name: 'Cambridge International Maths Online', image: `${IMG}/portfolio/cimo.png`,
                href: 'https://www.internationalmathsonline.com'},
            {name: 'Cambridge GCSE Maths Online', image: `${IMG}/portfolio/gcse.png`,
                href: 'https://gcsemaths.cambridge.org'},
            {name: 'Cambridge Senior Maths', image: `${IMG}/portfolio/senior.png`},
        ],
    },

    awards: {
        heading: 'Awards',
        items: [
            {title: '2014 Primary: Student Resource – Mathematics (Numeracy)', image: `${IMG}/APA.jpg`,
                body: 'Cambridge HOTmaths FUNdamentals iPad application.'},
            {title: 'Bett Award 2015 finalist', image: `${IMG}/BETT.jpg`,
                body: 'Cambridge Dynamic Science – Secondary Digital Content.'},
            {title: '2014 Tertiary and Vocational: Outstanding Digital Resource', image: `${IMG}/APA.jpg`,
                body: 'Primary Mathematics: Capitalising on ICT for Today and Tomorrow (HOTmaths component).'},
        ],
    },

    /** The old page's "Media": the HOTmaths video, mp4 and webm only, as on the HOTmaths homepage. */
    video: {
        heading: 'See Edjin in action',
        standfirst: 'See how Cambridge HOTmaths powered by Edjin is engaging students and supporting 21st century '
            + 'learning in the classroom.',
        poster: '/home/template/img/hotmaths-video.jpg',
        sources: [
            {src: 'https://cdn.edjin.com/home/hotmaths/videos/hotmaths-video.mp4', type: 'video/mp4'},
            {src: 'https://cdn.edjin.com/home/hotmaths/videos/hotmaths-video.webm', type: 'video/webm'},
        ],
    },

    contact: {
        heading: 'Contact us',
        body: 'Interested in building on Edjin, or have a question about the platform? Send us a message and we will '
            + 'be in touch.',
    },

    nav: [
        {label: 'Features', href: '#ch-edjin-features'},
        {label: 'Products', href: '#ch-edjin-products'},
        {label: 'Awards', href: '#ch-edjin-awards'},
        // Contact is the header's button (EdjinHome.jsx).
    ],
};

export default edjinContent;
